import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Upload, User, Save, CheckCircle2 } from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const InstructorProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    bio: '',
    expertise_area: '',
    phone: '',
    profile_image: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/instructor/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const instructor = response.data;
      setFormData({
        full_name: instructor.user?.full_name || '',
        bio: instructor.bio || '',
        expertise_area: instructor.specialties?.[0] || '',
        phone: instructor.phone || '',
        profile_image: instructor.profile_image || instructor.user?.profile_image || ''
      });
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error('Bu sayfaya erişim için onaylı eğitmen olmanız gerekiyor');
        navigate('/application-status');
      } else if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Profil yüklenirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Sadece JPG, PNG veya WebP dosyaları yüklenebilir');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(`${API}/upload/profile-image`, uploadData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ ...formData, profile_image: response.data.file_url });
      toast.success('Fotoğraf yüklendi');
    } catch (error) {
      toast.error('Fotoğraf yüklenirken hata oluştu');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/instructor/profile`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Profil başarıyla güncellendi!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Profil güncellenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 wellness-gradient">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/instructor/dashboard')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Dashboard'a Dön
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Profil Ayarları</CardTitle>
            <CardDescription>
              Eğitmen profilinizi güncelleyin. Bu bilgiler kurs detay sayfalarında görüntülenecek.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Profile Image */}
              <div className="flex items-center gap-6">
                <div className="relative">
                  {formData.profile_image ? (
                    <img
                      src={`${API}${formData.profile_image}`}
                      alt="Profil"
                      className="w-24 h-24 rounded-full object-cover border-4 border-purple-100"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-purple-100 flex items-center justify-center border-4 border-purple-200">
                      <User className="h-10 w-10 text-purple-400" />
                    </div>
                  )}
                  {uploadingImage && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-full">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-base font-medium">Profil Fotoğrafı</Label>
                  <p className="text-sm text-gray-500 mb-2">JPG, PNG veya WebP (Max 5MB)</p>
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                      <Upload className="h-4 w-4 mr-2" />
                      Fotoğraf Yükle
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.webp"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                  </label>
                </div>
              </div>

              {/* Name */}
              <div>
                <Label htmlFor="full_name">Ad Soyad *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Adınız Soyadınız"
                  required
                />
              </div>

              {/* Expertise */}
              <div>
                <Label>Uzmanlık Alanı *</Label>
                <Select
                  value={formData.expertise_area}
                  onValueChange={(value) => setFormData({ ...formData, expertise_area: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Uzmanlık alanınızı seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pilates">Pilates</SelectItem>
                    <SelectItem value="yoga">Yoga</SelectItem>
                    <SelectItem value="fitness">Fitness</SelectItem>
                    <SelectItem value="meditation">Meditasyon</SelectItem>
                    <SelectItem value="breathing">Nefes Teknikleri</SelectItem>
                    <SelectItem value="mobility">Mobilite</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="05XX XXX XX XX"
                />
                <p className="text-xs text-gray-500 mt-1">Bu bilgi yalnızca admin tarafından görüntülenebilir</p>
              </div>

              {/* Bio */}
              <div>
                <Label htmlFor="bio">Biyografi / Hakkımda</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={5}
                  placeholder="Kendinizi tanıtın, deneyimlerinizi ve uzmanlık alanlarınızı paylaşın..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Bu metin kurs detay sayfalarında eğitmen kartında görüntülenecek
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Değişiklikleri Kaydet
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InstructorProfile;
